// src/app/header/header.component.ts
import {Component, computed, HostListener, inject, signal} from '@angular/core';
import {NavItem} from '../../models/header.interface';
import {AsyncPipe, NgOptimizedImage} from '@angular/common';
import {DeviceService} from '../../ngm-dev-blocks/utils/services';
import {MatDialog} from '@angular/material/dialog';
import {AuthComponent} from '../../auth/auth/auth.component';
import {AuthService} from '../../auth/services/auth.service';
import {AuthQuery} from '../../auth/state/auth.query';
import {Router} from '@angular/router';
import {ScrollService} from '../../service/scroll.service';
import {LucideAngularModule} from 'lucide-angular';
import {LanguageService} from '../../service/language.service';
import {TranslatePipe} from '../../pipes/translate.pipe';

@Component({
  selector: 'app-header',
  imports: [
    NgOptimizedImage,
    AsyncPipe,
    LucideAngularModule,
    TranslatePipe,
  ],
  templateUrl: './header.component.html',
  standalone: true,
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  private deviceService = inject(DeviceService);
  private dialog = inject(MatDialog);
  private authQuery = inject(AuthQuery);
  private authService = inject(AuthService);
  private router = inject(Router);
  private scrollService = inject(ScrollService);
  langService = inject(LanguageService);

  // Auth signals
  isAuthenticated = this.authQuery.isAuthenticated;
  fullName = this.authQuery.fullName;
  userEmail = this.authQuery.userEmail;

  // Device observables
  isHandset$ = this.deviceService.isHandset$;
  isTablet$ = this.deviceService.isTablet$;
  isLessThanMD$ = this.deviceService.isLessThanMD$;

  // UI signals
  mobileMenuOpen = signal(false);
  activeSection = signal('installation');

  navItems = computed<NavItem[]>(() => [
    { label: this.langService.translate('nav.installation'), route: '/accueil', fragment: 'installation', external: false, scrollOffset: 80 },
    { label: this.langService.translate('nav.boutique'), route: '/accueil', fragment: 'boutique', external: false, scrollOffset: 80 },
    { label: this.langService.translate('nav.apropos'), route: '/accueil', fragment: 'apropos', external: false, scrollOffset: 30 },
    { label: this.langService.translate('nav.portfolio'), route: '/realisation', external: true },
  ]);

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.updateActiveSection();
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update(value => !value);
  }

  navigateToSection(item: NavItem) {
    console.log('🔵 Navigate clicked:', item);

    if (item.external && item.route) {
      this.router.navigate([item.route]);
      this.mobileMenuOpen.set(false);
      return;
    }

    if (item.fragment) {
      const offset = item.scrollOffset ?? 80;

      // If already on the page, just scroll
      if (this.router.url === '/accueil' || this.router.url === '/accueil#' + item.fragment) {
        this.scrollService.scrollToSection(item.fragment, offset);
        this.activeSection.set(item.fragment);
        this.mobileMenuOpen.set(false);
      } else {
        // Navigate first, then scroll
        this.router.navigate([item.route || '/accueil'], { fragment: item.fragment }).then(() => {
          setTimeout(() => {
            this.scrollService.scrollToSection(item.fragment!, offset);
            this.activeSection.set(item.fragment!);
          }, 300); // Wait for navigation to complete
        });
        this.mobileMenuOpen.set(false);
      }
    }
  }

  navigateToDevis() {
    this.router.navigate(['/devis'], { fragment: 'quote-form' });
    this.mobileMenuOpen.set(false);
  }

  openAuthDialog() {
    this.dialog.open(AuthComponent, {
      width: 'auto',
      maxWidth: '1200px',
      minWidth: '320px',
      height: 'auto',
      maxHeight: '90vh',
      panelClass: 'auth-dialog',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'auth-backdrop-blur'
    });
  }

  logout() {
    this.authService.logout();
  }

  private updateActiveSection() {
    // Only update active section if we're on the home page
    if (this.router.url === '/accueil' || this.router.url.startsWith('/accueil#')) {
      const sections = ['installation', 'boutique', 'apropos'];
      const scrollPosition = window.pageYOffset + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            this.activeSection.set(sectionId);
            break;
          }
        }
      }
    }
  }
}
